import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/layout';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useGetMe } from '@workspace/api-client-react';
import { useLocation } from 'wouter';
import { DynamicWheelStock } from '@/components/dynamic-wheel-stock';
import { canUseDynamicWheel } from '@/lib/supplier-access';
import { Database, Loader2, CheckCircle2, XCircle, Clock, RefreshCcw, AlertTriangle, KeyRound, Save, MapPin, Hash, Search, Package, Activity, ChevronDown, ChevronUp } from 'lucide-react';

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, '') ?? '';

interface Store {
  id: string;
  nickname: string;
  storeUrl: string;
}

interface StoreLocation {
  id: string;
  name: string;
  active: boolean;
}

interface SupplierConnection {
  id: string;
  storeId: string;
  supplierName: string;
  supplierShopifyUrl: string;
  platform: string;
  inventoryEnabled: boolean;
  markOosWhenMissing: boolean;
  supplierAdminToken: string | null;
  supplierLocationId: string | null;
  targetLocationId: string | null;
  maxInventoryCap: number | null;
}

interface SyncProgress {
  phase: 'supplier_fetch' | 'store_fetch' | 'updating';
  supplierPagesDone?: number;
  storePagesDone?: number;
  updatedSoFar?: number;
  totalToUpdate?: number;
}

interface InventoryRun {
  id: string;
  supplierId: string;
  startedAt: string;
  completedAt: string | null;
  status: 'running' | 'done' | 'error';
  supplierSkuCount: number | null;
  matchedCount: number | null;
  updatedCount: number | null;
  unmatchedSupplierCount: number | null;
  unmatchedTargetCount: number | null;
  skippedUntracked: number | null;
  errorMessage: string | null;
  progress: SyncProgress | null;
}

interface ActiveSyncRun extends InventoryRun {
  supplierName: string;
  storeName: string;
}

// ── Progress indicator ────────────────────────────────────────────────────────

const PHASES = [
  { key: 'supplier_fetch', label: 'Fetching supplier catalogue' },
  { key: 'store_fetch',    label: 'Reading store catalogue' },
  { key: 'updating',       label: 'Updating inventory levels' },
] as const;

function SyncProgressIndicator({ progress }: { progress: SyncProgress | null }) {
  const activeIdx = progress
    ? PHASES.findIndex((p) => p.key === progress.phase)
    : 0;

  const sublabel = (phase: typeof PHASES[number]['key']): string => {
    if (!progress || progress.phase !== phase) return '';
    if (phase === 'supplier_fetch') {
      const pages = progress.supplierPagesDone ?? 0;
      return pages > 0 ? `${pages} page${pages === 1 ? '' : 's'} fetched` : 'Starting…';
    }
    if (phase === 'store_fetch') {
      const pages = progress.storePagesDone ?? 0;
      return pages > 0 ? `${pages} page${pages === 1 ? '' : 's'} fetched` : 'Starting…';
    }
    if (phase === 'updating') {
      const done = progress.updatedSoFar ?? 0;
      const total = progress.totalToUpdate ?? 0;
      if (total > 0) {
        const pct = Math.round((done / total) * 100);
        return `${done.toLocaleString()} / ${total.toLocaleString()} (${pct}%)`;
      }
      return 'Starting…';
    }
    return '';
  };

  // Progress bar fill for the updating phase
  const barPct = progress?.phase === 'updating' && (progress.totalToUpdate ?? 0) > 0
    ? Math.round(((progress.updatedSoFar ?? 0) / progress.totalToUpdate!) * 100)
    : null;

  return (
    <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 px-4 py-3 space-y-3">
      <div className="flex items-center gap-2 text-xs text-violet-300 font-medium">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Sync in progress
      </div>

      <ol className="space-y-2">
        {PHASES.map((phase, idx) => {
          const isDone    = idx < activeIdx;
          const isActive  = idx === activeIdx;
          const isPending = idx > activeIdx;
          return (
            <li key={phase.key} className="flex items-start gap-3">
              {/* Step icon */}
              <div className="shrink-0 mt-0.5">
                {isDone ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                ) : isActive ? (
                  <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-border" />
                )}
              </div>
              {/* Label + sublabel */}
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-medium ${isDone ? 'text-muted-foreground' : isActive ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                  {phase.label}
                </div>
                {isActive && sublabel(phase.key) && (
                  <div className="text-xs text-violet-300/80 mt-0.5">{sublabel(phase.key)}</div>
                )}
                {/* Progress bar for the updating phase */}
                {isActive && phase.key === 'updating' && barPct !== null && (
                  <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-violet-500 transition-all duration-500"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ── Active syncs panel ────────────────────────────────────────────────────────

function ActiveSyncsPanel({ runs }: { runs: ActiveSyncRun[] }) {
  if (runs.length === 0) return null;

  return (
    <div className="rounded-xl border border-violet-500/30 bg-violet-500/8 overflow-hidden">
      {/* Panel header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-violet-500/20">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-500" />
        </span>
        <Activity className="w-3.5 h-3.5 text-violet-400" />
        <span className="text-xs font-semibold text-violet-300">
          {runs.length === 1 ? 'Active sync' : `${runs.length} active syncs`}
        </span>
      </div>

      {/* One row per active sync */}
      <div className="divide-y divide-violet-500/10">
        {runs.map((run) => {
          const activeIdx = run.progress
            ? PHASES.findIndex((p) => p.key === run.progress!.phase)
            : 0;

          const sublabel = (): string => {
            if (!run.progress) return 'Starting…';
            const { phase } = run.progress;
            if (phase === 'supplier_fetch') {
              const pages = run.progress.supplierPagesDone ?? 0;
              return pages > 0 ? `${pages} page${pages === 1 ? '' : 's'} fetched` : 'Starting…';
            }
            if (phase === 'store_fetch') {
              const pages = run.progress.storePagesDone ?? 0;
              return pages > 0 ? `${pages} page${pages === 1 ? '' : 's'} fetched` : 'Starting…';
            }
            if (phase === 'updating') {
              const done = run.progress.updatedSoFar ?? 0;
              const total = run.progress.totalToUpdate ?? 0;
              if (total > 0) {
                const pct = Math.round((done / total) * 100);
                return `${done.toLocaleString()} / ${total.toLocaleString()} (${pct}%)`;
              }
              return 'Starting…';
            }
            return '';
          };

          const barPct =
            run.progress?.phase === 'updating' && (run.progress.totalToUpdate ?? 0) > 0
              ? Math.round(((run.progress.updatedSoFar ?? 0) / run.progress.totalToUpdate!) * 100)
              : null;

          const started = new Date(run.startedAt);
          const elapsed = Math.floor((Date.now() - started.getTime()) / 1000);
          const elapsedLabel =
            elapsed < 60
              ? `${elapsed}s`
              : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;

          return (
            <div key={run.id} className="px-4 py-3 space-y-2.5">
              {/* Supplier + store */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-violet-400" />
                  <span className="text-sm font-medium text-foreground truncate">{run.supplierName}</span>
                  <span className="text-xs text-muted-foreground shrink-0">→ {run.storeName}</span>
                </div>
                <span className="text-xs text-muted-foreground shrink-0 tabular-nums">{elapsedLabel}</span>
              </div>

              {/* Phase steps — compact horizontal layout */}
              <div className="flex items-center gap-1">
                {PHASES.map((phase, idx) => {
                  const isDone   = idx < activeIdx;
                  const isActive = idx === activeIdx;
                  return (
                    <React.Fragment key={phase.key}>
                      {idx > 0 && (
                        <div className={`flex-1 h-px ${isDone || isActive ? 'bg-violet-500/40' : 'bg-muted'}`} />
                      )}
                      <div className="flex items-center gap-1 shrink-0">
                        {isDone ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        ) : isActive ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border border-border" />
                        )}
                        <span className={`text-xs ${isDone ? 'text-muted-foreground' : isActive ? 'text-foreground' : 'text-muted-foreground/40'}`}>
                          {phase.label}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Detail label + optional progress bar */}
              {(sublabel() || barPct !== null) && (
                <div className="space-y-1">
                  <p className="text-xs text-violet-300/80">{sublabel()}</p>
                  {barPct !== null && (
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-violet-500 transition-all duration-500"
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function InventorySync() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const [location, setLocation] = useLocation();
  const isDynamicWheelPage = location === '/settings/dynamic-wheel';

  const accountQueryScope = React.useMemo(() => crypto.randomUUID(), [token]);
  const { data: me, isLoading: meLoading } = useGetMe({
    request: authOpts,
    query: { enabled: !!token, retry: false, queryKey: ['/api/auth/me', 'inventory-sync', accountQueryScope] },
  } as any);
  const canAccessDynamicWheel = canUseDynamicWheel(me?.email);

  // Feature flag gate
  useEffect(() => {
    if (!meLoading && me && !(me as any).featureFlags?.inventory_sync) {
      setLocation('/');
    } else if (!meLoading && me && isDynamicWheelPage && !canAccessDynamicWheel) {
      setLocation('/settings/inventory-sync');
    }
  }, [meLoading, me, setLocation, isDynamicWheelPage, canAccessDynamicWheel]);

  const headers: Record<string, string> = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [stores, setStores] = useState<Store[]>([]);
  const [connections, setConnections] = useState<SupplierConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeRuns, setActiveRuns] = useState<ActiveSyncRun[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [invRuns, setInvRuns] = useState<Record<string, InventoryRun[]>>({});
  const [invRunsLoading, setInvRunsLoading] = useState<Record<string, boolean>>({});
  const [invSyncBusy, setInvSyncBusy] = useState<Record<string, boolean>>({});
  const [syncTimeouts, setSyncTimeouts] = useState<Record<string, boolean>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  // Draft values for admin token / location ID inputs (unsaved edits)
  const [adminTokenDraft, setAdminTokenDraft] = useState<Record<string, string>>({});
  const [locationIdDraft, setLocationIdDraft] = useState<Record<string, string>>({});
  const [adminApiSaving, setAdminApiSaving] = useState<Record<string, boolean>>({});
  // SKU checker
  const [skuInput, setSkuInput] = useState<Record<string, string>>({});
  const [skuChecking, setSkuChecking] = useState<Record<string, boolean>>({});
  interface SkuCheckResult { found: boolean; sku: string; qty: number | null; method: string; note?: string; error?: string; }
  const [skuResult, setSkuResult] = useState<Record<string, SkuCheckResult>>({});

  // Target location picker
  const [storeLocations, setStoreLocations] = useState<Record<string, StoreLocation[]>>({});
  const [locationsLoading, setLocationsLoading] = useState<Record<string, boolean>>({});
  const [targetLocDraft, setTargetLocDraft] = useState<Record<string, string>>({});
  // Inventory cap
  const [capDraft, setCapDraft] = useState<Record<string, string>>({});
  const [targetSettingsSaving, setTargetSettingsSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setStores([]);
    setConnections([]);
    setLoading(true);
    setLoadError(null);
    Promise.allSettled([
      fetch(`${BASE}/api/stores`, { headers }).then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error ?? 'Failed to load stores');
        return data;
      }),
      isDynamicWheelPage ? Promise.resolve({ connections: [] }) : fetch(`${BASE}/api/shipping/suppliers`, { headers }).then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error ?? 'Failed to load supplier connections');
        return data;
      }),
    ]).then(([storeResult, supplierResult]) => {
      if (cancelled) return;
      if (storeResult.status === 'fulfilled') {
        const storeData = storeResult.value;
        setStores(storeData?.stores ?? storeData ?? []);
      }
      if (supplierResult.status === 'fulfilled') {
        const supplierData = supplierResult.value;
        const all: SupplierConnection[] = supplierData?.connections ?? supplierData?.suppliers ?? [];
        setConnections(all.filter((c) => !c.platform || c.platform === 'shopify'));
      }
      const errors = [storeResult, supplierResult].flatMap(result => result.status === 'rejected' ? [String(result.reason?.message ?? 'Failed to load inventory settings')] : []);
      setLoadError(errors.length ? errors.join(' ') : null);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, isDynamicWheelPage]);

  // Refs to detect when a run leaves the active list so we can refresh that supplier's history
  const prevActiveRunsRef = React.useRef<ActiveSyncRun[]>([]);

  // Poll for any active syncs every 3 s — catches runs started outside this session
  useEffect(() => {
    if (!token || isDynamicWheelPage) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(`${BASE}/api/shipping/inventory-runs/active`, {
          headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const runs: ActiveSyncRun[] = data.runs ?? [];
        setActiveRuns(runs);
      } catch { /* ignore network errors */ }
    };

    poll();
    const timer = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [token, isDynamicWheelPage]);

  // When a run disappears from the active list, refresh that supplier's run history
  useEffect(() => {
    const prev = prevActiveRunsRef.current;
    const currentIds = new Set(activeRuns.map((r) => r.id));
    prev.forEach((run) => {
      if (!currentIds.has(run.id)) fetchInvRuns(run.supplierId);
    });
    prevActiveRunsRef.current = activeRuns;
  }, [activeRuns]);

  // Fetch runs for all suppliers on mount
  useEffect(() => {
    if (!token || connections.length === 0) return;
    connections.forEach((conn) => fetchInvRuns(conn.id));
  }, [connections.map((c) => c.id).join(',')]);

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const storeMap = new Map(stores.map((s) => [s.id, s]));

  async function fetchInvRuns(connId: string): Promise<InventoryRun[]> {
    setInvRunsLoading((s) => ({ ...s, [connId]: true }));
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers/${connId}/inventory-runs`, {
        headers: { ...headers, 'Cache-Control': 'no-cache' },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (!Array.isArray(data.runs)) throw new Error('Invalid inventory run history response');
        const runs: InventoryRun[] = data.runs;
        setInvRuns((s) => ({ ...s, [connId]: runs }));
        return runs;
      }
      throw new Error(data.error ?? 'Failed to load inventory run history');
    } catch { return []; } finally {
      setInvRunsLoading((s) => ({ ...s, [connId]: false }));
    }
  }

  async function pollInvRuns(connId: string, attempt = 0) {
    if (attempt > 400) {
      setInvSyncBusy((s) => ({ ...s, [connId]: false }));
      setSyncTimeouts((s) => ({ ...s, [connId]: true }));
      return;
    }
    const runs = await fetchInvRuns(connId);
    const latest = runs[0];
    if (!latest || latest.status === 'running') {
      setTimeout(() => pollInvRuns(connId, attempt + 1), 3000);
    } else {
      setInvSyncBusy((s) => ({ ...s, [connId]: false }));
    }
  }

  async function triggerSync(conn: SupplierConnection) {
    setInvSyncBusy((s) => ({ ...s, [conn.id]: true }));
    setSyncTimeouts((s) => ({ ...s, [conn.id]: false }));
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers/${conn.id}/sync-inventory`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? 'Sync failed');
      setInvRuns((s) => ({
        ...s,
        [conn.id]: [data.run, ...(s[conn.id] ?? [])].slice(0, 10),
      }));
      pollInvRuns(conn.id, 0);
    } catch (e: any) {
      alert(e.message);
      setInvSyncBusy((s) => ({ ...s, [conn.id]: false }));
    }
  }

  async function saveSetting(
    conn: SupplierConnection,
    field: 'inventoryEnabled' | 'markOosWhenMissing',
    value: boolean,
  ) {
    const res = await fetch(`${BASE}/api/shipping/suppliers/${conn.id}`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    });
    if (res.ok) {
      setConnections((cs) =>
        cs.map((c) => (c.id === conn.id ? { ...c, [field]: value } : c)),
      );
    }
  }

  async function checkSku(conn: SupplierConnection) {
    const sku = (skuInput[conn.id] ?? '').trim();
    if (!sku) return;
    setSkuChecking((s) => ({ ...s, [conn.id]: true }));
    setSkuResult((s) => { const n = { ...s }; delete n[conn.id]; return n; });
    try {
      const res = await fetch(
        `${BASE}/api/shipping/suppliers/${conn.id}/check-sku?sku=${encodeURIComponent(sku)}`,
        { headers },
      );
      const data = await res.json();
      if (!res.ok) {
        setSkuResult((s) => ({ ...s, [conn.id]: { found: false, sku, qty: null, method: '', error: data.error ?? 'Lookup failed' } }));
      } else {
        setSkuResult((s) => ({ ...s, [conn.id]: data }));
      }
    } catch {
      setSkuResult((s) => ({ ...s, [conn.id]: { found: false, sku, qty: null, method: '', error: 'Network error' } }));
    } finally {
      setSkuChecking((s) => ({ ...s, [conn.id]: false }));
    }
  }

  async function loadStoreLocations(conn: SupplierConnection) {
    setLocationsLoading((s) => ({ ...s, [conn.id]: true }));
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers/${conn.id}/store-locations`, { headers });
      const data = await res.json();
      if (res.ok) {
        setStoreLocations((s) => ({ ...s, [conn.id]: data.locations ?? [] }));
      } else {
        alert(data.error ?? 'Failed to load locations');
      }
    } catch { alert('Failed to load locations'); }
    finally { setLocationsLoading((s) => ({ ...s, [conn.id]: false })); }
  }

  async function saveTargetSettings(conn: SupplierConnection) {
    const locId = targetLocDraft[conn.id] !== undefined
      ? (targetLocDraft[conn.id] || null)
      : conn.targetLocationId;
    const capRaw = capDraft[conn.id] !== undefined
      ? capDraft[conn.id].trim()
      : (conn.maxInventoryCap != null ? String(conn.maxInventoryCap) : '');
    const cap = capRaw === '' ? null : parseInt(capRaw, 10);
    setTargetSettingsSaving((s) => ({ ...s, [conn.id]: true }));
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers/${conn.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetLocationId: locId, maxInventoryCap: cap }),
      });
      if (res.ok) {
        setConnections((cs) => cs.map((c) => c.id === conn.id
          ? { ...c, targetLocationId: locId, maxInventoryCap: (cap && !isNaN(cap)) ? cap : null }
          : c));
        setTargetLocDraft((s) => { const n = { ...s }; delete n[conn.id]; return n; });
        setCapDraft((s) => { const n = { ...s }; delete n[conn.id]; return n; });
      }
    } finally {
      setTargetSettingsSaving((s) => ({ ...s, [conn.id]: false }));
    }
  }

  async function saveAdminApi(conn: SupplierConnection) {
    const tok = (adminTokenDraft[conn.id] ?? conn.supplierAdminToken ?? '').trim();
    const loc = (locationIdDraft[conn.id] ?? conn.supplierLocationId ?? '').trim();
    setAdminApiSaving((s) => ({ ...s, [conn.id]: true }));
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers/${conn.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplierAdminToken: tok || null,
          supplierLocationId: loc || null,
        }),
      });
      if (res.ok) {
        setConnections((cs) =>
          cs.map((c) =>
            c.id === conn.id
              ? { ...c, supplierAdminToken: tok || null, supplierLocationId: loc || null }
              : c,
          ),
        );
        // Clear drafts now that saved values match
        setAdminTokenDraft((s) => { const n = { ...s }; delete n[conn.id]; return n; });
        setLocationIdDraft((s) => { const n = { ...s }; delete n[conn.id]; return n; });
      }
    } finally {
      setAdminApiSaving((s) => ({ ...s, [conn.id]: false }));
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout collapseMobileNav>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const shopifyConns = connections.filter((c) => c.platform === 'shopify');

  if (isDynamicWheelPage) {
    return (
      <Layout collapseMobileNav>
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dynamic Wheel Co API</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Read Dynamic Wheel Co stock quantities and update your store by SKU after reviewing a preview.
            </p>
          </div>
          {loadError && <p role="alert" className="text-sm text-red-300">{loadError}</p>}
          {canAccessDynamicWheel && <DynamicWheelStock key={token ?? 'anon'} token={token} stores={stores} />}
        </div>
      </Layout>
    );
  }

  return (
    <Layout collapseMobileNav>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Database className="w-6 h-6 text-violet-400" />
            {isDynamicWheelPage ? 'Dynamic Wheel Co API' : 'Inventory Sync'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isDynamicWheelPage
              ? "Read Dynamic Wheel Co stock quantities and update your store by SKU after reviewing a preview."
              : canAccessDynamicWheel
              ? "Pull stock quantities from Dynamic Wheel Co or a supplier's public Shopify store and update your store by SKU."
              : "Pull stock quantities from a supplier's public Shopify store and update your store by SKU."}
          </p>
        </div>

        {canAccessDynamicWheel && <DynamicWheelStock key={token ?? 'anon'} token={token} stores={stores} />}

        {/* Active syncs panel — shown whenever any sync is running */}
        <ActiveSyncsPanel runs={activeRuns} />

        {loadError && (
          <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Could not load inventory sync data: {loadError}
          </div>
        )}

        {/* No Shopify suppliers */}
        {shopifyConns.length === 0 && (
          <div className="bg-muted/30 border border-border rounded-xl p-8 text-center">
            <Database className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No Shopify supplier connections found.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add a Shopify supplier in{' '}
              <a href={`${BASE}/settings/shipping-rates`} className="text-primary hover:underline">
                Live Supplier Rates
              </a>{' '}
              first.
            </p>
          </div>
        )}

        {/* Supplier cards */}
        <div className="space-y-3">
          {shopifyConns.map((conn) => {
            const store = storeMap.get(conn.storeId);
            const busy = !!invSyncBusy[conn.id];
            const runs = invRuns[conn.id] ?? [];
            const latestRun = runs[0];
            const isExpanded = expandedId === conn.id;

            return (
              <div
                key={conn.id}
                className="bg-muted/30 border border-border rounded-xl overflow-hidden"
              >
                {syncTimeouts[conn.id] && (
                  <div className="flex items-start gap-2 border-b border-amber-500/20 bg-amber-500/10 px-4 py-3 text-xs text-amber-300">
                    <AlertTriangle className="mt-0.5 w-3.5 h-3.5 shrink-0" />
                    <span>Sync status polling timed out. The sync may still be running; refresh this page to check its latest status before starting another sync.</span>
                  </div>
                )}
                {/* ── Collapsed header row ── */}
                <div className="flex items-center gap-3 px-4 py-3">

                  {/* Status dot */}
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                    conn.inventoryEnabled ? 'bg-emerald-400' : 'bg-muted'
                  }`} />

                  {/* Name + URL + store — clickable to expand */}
                  <button
                    className="flex-1 min-w-0 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : conn.id)}
                  >
                    <div className="text-sm font-medium text-foreground truncate">{conn.supplierName}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span className="truncate">{conn.supplierShopifyUrl}</span>
                      {store && (
                        <>
                          <span>·</span>
                          <span className="shrink-0">{store.nickname}</span>
                        </>
                      )}
                    </div>
                  </button>

                  {/* Status chips */}
                  <div className="hidden sm:flex items-center gap-2 shrink-0">
                    {/* Enabled/disabled badge */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      conn.inventoryEnabled
                        ? 'bg-emerald-500/15 text-emerald-400'
                        : 'bg-muted/60 text-muted-foreground'
                    }`}>
                      {conn.inventoryEnabled ? 'Enabled' : 'Disabled'}
                    </span>

                    {/* Last run result */}
                    {latestRun && (
                      latestRun.status === 'running' ? (
                        <span className="flex items-center gap-1 text-xs text-violet-400">
                          <Loader2 className="w-3 h-3 animate-spin" /> Syncing
                        </span>
                      ) : latestRun.status === 'done' ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-400">
                          <CheckCircle2 className="w-3 h-3" />
                          {latestRun.updatedCount ?? 0} updated
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-red-400">
                          <XCircle className="w-3 h-3" /> Error
                        </span>
                      )
                    )}
                  </div>

                  {/* Sync Now — always visible so you don't need to expand first */}
                  {conn.inventoryEnabled && (
                    <button
                      onClick={() => triggerSync(conn)}
                      disabled={busy}
                      className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-500/15 text-violet-300 border border-violet-500/25 hover:bg-violet-500/25 disabled:opacity-50 transition-colors"
                    >
                      {busy
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Syncing…</>
                        : <><RefreshCcw className="w-3.5 h-3.5" /> Sync Now</>
                      }
                    </button>
                  )}

                  {/* Expand/collapse toggle */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : conn.id)}
                    className="shrink-0 p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title={isExpanded ? 'Collapse' : 'Expand settings'}
                  >
                    {isExpanded
                      ? <ChevronUp className="w-4 h-4" />
                      : <ChevronDown className="w-4 h-4" />
                    }
                  </button>
                </div>

                {/* ── Expanded settings body ── */}
                {isExpanded && (
                  <div className="border-t border-border/50 px-5 py-4 space-y-4">

                    {/* Enable toggle */}
                    <label className="flex items-start gap-3 cursor-pointer">
                      <div
                        onClick={() => saveSetting(conn, 'inventoryEnabled', !conn.inventoryEnabled)}
                        className={`relative mt-0.5 shrink-0 w-9 h-5 rounded-full transition-colors cursor-pointer ${
                          conn.inventoryEnabled ? 'bg-violet-500' : 'bg-muted'
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          conn.inventoryEnabled ? 'translate-x-4' : ''
                        }`} />
                      </div>
                      <div>
                        <div className="text-sm text-foreground font-medium">Enable inventory sync</div>
                        <div className="text-xs text-muted-foreground">
                          Match SKUs from this supplier's public catalogue and update your store's stock levels.
                        </div>
                      </div>
                    </label>

                    {/* OOS checkbox — only when enabled */}
                    {conn.inventoryEnabled && (
                      <label className="flex items-start gap-3 cursor-pointer ml-12">
                        <input
                          type="checkbox"
                          checked={conn.markOosWhenMissing}
                          onChange={(e) => saveSetting(conn, 'markOosWhenMissing', e.target.checked)}
                          className="mt-1 accent-violet-500"
                        />
                        <div>
                          <div className="text-sm text-foreground">Mark unmapped variants as out of stock</div>
                          <div className="text-xs text-muted-foreground">
                            Variants in your store whose SKU doesn't appear in the supplier catalogue will be set to qty 0.
                          </div>
                        </div>
                      </label>
                    )}

                    {/* Live progress — shown when a sync is running */}
                    {latestRun && latestRun.status === 'running' && (
                      <SyncProgressIndicator progress={latestRun.progress} />
                    )}

                    {/* Latest result detail */}
                    {latestRun && latestRun.status === 'done' && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { label: 'Supplier SKUs', value: latestRun.supplierSkuCount ?? '—', color: 'text-foreground' },
                            { label: 'Matched', value: latestRun.matchedCount ?? '—', color: 'text-emerald-400' },
                            { label: 'Updated', value: latestRun.updatedCount ?? '—', color: 'text-violet-400' },
                            {
                              label: 'Unmatched (supplier)',
                              value: latestRun.unmatchedSupplierCount ?? '—',
                              color: (latestRun.unmatchedSupplierCount ?? 0) > 0 ? 'text-amber-400' : 'text-foreground',
                            },
                          ].map(({ label, value, color }) => (
                            <div key={label} className="bg-muted/30 rounded-lg px-3 py-2">
                              <div className="text-xs text-muted-foreground">{label}</div>
                              <div className={`text-lg font-semibold ${color}`}>{value}</div>
                            </div>
                          ))}
                        </div>
                        {(latestRun.skippedUntracked ?? 0) > 0 && (
                          <div className="flex items-start gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <div>
                              <span className="font-medium">{latestRun.skippedUntracked} supplier variant{latestRun.skippedUntracked === 1 ? '' : 's'} skipped</span>
                              {' '}— the supplier's store doesn't track those products through Shopify's inventory system (they use a 3rd-party or manual system), so no reliable stock quantity is available via the public API. Only Shopify-managed variants can be synced.
                            </div>
                          </div>
                        )}
                        {(latestRun.supplierSkuCount === 0) && (latestRun.skippedUntracked ?? 0) > 0 && (
                          <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                            <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                            <div>
                              No Shopify-tracked inventory found in this supplier's catalogue. No quantities were updated. The supplier likely manages stock outside of Shopify.
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {latestRun && latestRun.status === 'error' && (
                      <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{latestRun.errorMessage ?? 'Unknown error'}</span>
                      </div>
                    )}

                    {/* Supplier Admin API — optional */}
                    <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3 space-y-3">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <KeyRound className="w-3.5 h-3.5" />
                        Supplier Admin API
                        {conn.supplierAdminToken && (
                          <span className="ml-auto text-emerald-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Configured
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        If the supplier's public storefront hides inventory quantities, add their Admin API token and location ID to pull real stock levels directly.
                      </p>
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Admin API token</label>
                          <input
                            type="password"
                            placeholder="shpat_…"
                            value={adminTokenDraft[conn.id] ?? conn.supplierAdminToken ?? ''}
                            onChange={(e) => setAdminTokenDraft((s) => ({ ...s, [conn.id]: e.target.value }))}
                            className="w-full bg-muted/50 border border-border/60 rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Location ID</label>
                          <input
                            type="text"
                            placeholder="e.g. 76924682456"
                            value={locationIdDraft[conn.id] ?? conn.supplierLocationId ?? ''}
                            onChange={(e) => setLocationIdDraft((s) => ({ ...s, [conn.id]: e.target.value }))}
                            className="w-full bg-muted/50 border border-border/60 rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                          />
                        </div>
                        <button
                          onClick={() => saveAdminApi(conn)}
                          disabled={!!adminApiSaving[conn.id]}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/60 text-foreground border border-border/60 hover:bg-muted disabled:opacity-50 transition-colors"
                        >
                          {adminApiSaving[conn.id]
                            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                            : <><Save className="w-3.5 h-3.5" /> Save</>
                          }
                        </button>
                      </div>
                    </div>

                    {/* Target location + inventory cap */}
                    <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3 space-y-3">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5" />
                        Target Location
                        {conn.targetLocationId && (
                          <span className="ml-auto text-emerald-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            {storeLocations[conn.id]?.find((l) => l.id === conn.targetLocationId)?.name ?? conn.targetLocationId}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Choose which location on your store to write inventory levels to. Defaults to the first active location if not set.
                      </p>
                      <div className="flex gap-2 items-start">
                        <div className="flex-1">
                          {storeLocations[conn.id]?.length ? (
                            <select
                              value={targetLocDraft[conn.id] ?? conn.targetLocationId ?? ''}
                              onChange={(e) => setTargetLocDraft((s) => ({ ...s, [conn.id]: e.target.value }))}
                              className="w-full bg-muted/50 border border-border/60 rounded-lg px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                            >
                              <option value="">— Use first active location (default) —</option>
                              {storeLocations[conn.id].map((loc) => (
                                <option key={loc.id} value={loc.id}>
                                  {loc.name}{!loc.active ? ' (inactive)' : ''}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <button
                              onClick={() => loadStoreLocations(conn)}
                              disabled={!!locationsLoading[conn.id]}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/60 text-foreground border border-border/60 hover:bg-muted disabled:opacity-50 transition-colors"
                            >
                              {locationsLoading[conn.id]
                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</>
                                : <><MapPin className="w-3.5 h-3.5" /> Load locations from store</>
                              }
                            </button>
                          )}
                        </div>
                        {storeLocations[conn.id]?.length ? (
                          <button
                            onClick={() => loadStoreLocations(conn)}
                            disabled={!!locationsLoading[conn.id]}
                            className="shrink-0 px-2 py-1.5 rounded-lg text-xs bg-muted/50 border border-border/60 hover:bg-muted text-muted-foreground disabled:opacity-50 transition-colors"
                            title="Refresh locations"
                          >
                            <RefreshCcw className={`w-3.5 h-3.5 ${locationsLoading[conn.id] ? 'animate-spin' : ''}`} />
                          </button>
                        ) : null}
                      </div>

                      {/* Inventory cap */}
                      <div className="pt-2 border-t border-border/50 space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                          <Hash className="w-3.5 h-3.5" />
                          Inventory cap
                          {conn.maxInventoryCap != null && (
                            <span className="ml-auto text-amber-400">max {conn.maxInventoryCap.toLocaleString()}</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Some suppliers set artificially high numbers (e.g. 1,000,000) to indicate "always in stock". Set a cap to clamp those values before they're written to your store.
                        </p>
                        <input
                          type="number"
                          min="1"
                          placeholder="e.g. 999 — leave blank for no cap"
                          value={capDraft[conn.id] ?? (conn.maxInventoryCap != null ? String(conn.maxInventoryCap) : '')}
                          onChange={(e) => setCapDraft((s) => ({ ...s, [conn.id]: e.target.value }))}
                          className="w-full bg-muted/50 border border-border/60 rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                        />
                      </div>

                      {(targetLocDraft[conn.id] !== undefined || capDraft[conn.id] !== undefined) && (
                        <button
                          onClick={() => saveTargetSettings(conn)}
                          disabled={!!targetSettingsSaving[conn.id]}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-500/20 text-violet-300 border border-violet-500/30 hover:bg-violet-500/30 disabled:opacity-50 transition-colors"
                        >
                          {targetSettingsSaving[conn.id]
                            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                            : <><Save className="w-3.5 h-3.5" /> Save changes</>
                          }
                        </button>
                      )}
                    </div>

                    {/* SKU checker */}
                    <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3 space-y-3">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <Search className="w-3.5 h-3.5" />
                        SKU Stock Checker
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Look up the current stock count for a specific SKU on the supplier's store.
                        {!conn.supplierAdminToken && ' For real quantities, add Admin API credentials above.'}
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter a SKU…"
                          value={skuInput[conn.id] ?? ''}
                          onChange={(e) => setSkuInput((s) => ({ ...s, [conn.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && checkSku(conn)}
                          className="flex-1 bg-muted/50 border border-border/60 rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                        />
                        <button
                          onClick={() => checkSku(conn)}
                          disabled={!!skuChecking[conn.id] || !(skuInput[conn.id] ?? '').trim()}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/60 text-foreground border border-border/60 hover:bg-muted disabled:opacity-50 transition-colors"
                        >
                          {skuChecking[conn.id]
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Search className="w-3.5 h-3.5" />
                          }
                          Check
                        </button>
                      </div>
                      {skuResult[conn.id] && (() => {
                        const r = skuResult[conn.id]!;
                        if (r.error) return (
                          <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                            <XCircle className="w-3.5 h-3.5 shrink-0" />
                            {r.error}
                          </div>
                        );
                        if (!r.found) return (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 border border-border/60 rounded-lg px-3 py-2">
                            <Package className="w-3.5 h-3.5 shrink-0" />
                            <span>SKU <span className="font-mono text-foreground">{r.sku}</span> was not found in the supplier catalogue.</span>
                          </div>
                        );
                        const isHidden = r.qty === null;
                        const isOos = !isHidden && r.qty === 0;
                        return (
                          <div className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border text-xs ${
                            isHidden ? 'bg-muted/30 border-border/60 text-muted-foreground'
                            : isOos   ? 'bg-red-500/10 border-red-500/20 text-red-700 dark:text-red-300'
                                      : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                          }`}>
                            <span className={`text-2xl font-bold tabular-nums ${isHidden ? 'text-muted-foreground' : isOos ? 'text-red-400' : 'text-emerald-400'}`}>
                              {isHidden ? '?' : r.qty}
                            </span>
                            <div>
                              <div className="font-medium text-foreground">
                                {isHidden ? 'Quantity hidden' : isOos ? 'Out of stock' : 'In stock'}
                              </div>
                              <div className="text-muted-foreground">
                                SKU <span className="font-mono">{r.sku}</span>
                                {' · '}{r.method === 'admin-api' ? 'via Admin API' : 'via public storefront'}
                              </div>
                              {r.note && <div className="mt-0.5 text-amber-400">{r.note}</div>}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Run history */}
                    {runs.length > 0 && (
                      <details className="group">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1 select-none">
                          <Clock className="w-3 h-3" /> Run history ({runs.length})
                        </summary>
                        <div className="mt-2 space-y-1">
                          {runs.map((run) => (
                            <div
                              key={run.id}
                              className="flex items-start gap-3 px-3 py-2 rounded-lg bg-muted/30 text-xs"
                            >
                              <div className="shrink-0 mt-0.5">
                                {run.status === 'running' ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400" />
                                ) : run.status === 'done' ? (
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5 text-red-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-muted-foreground">
                                  {new Date(run.startedAt).toLocaleString('en-AU', {
                                    dateStyle: 'short',
                                    timeStyle: 'short',
                                  })}
                                  {run.completedAt && (
                                    <span className="ml-2">
                                      ({Math.round(
                                        (new Date(run.completedAt).getTime() -
                                          new Date(run.startedAt).getTime()) /
                                          1000,
                                      )}s)
                                    </span>
                                  )}
                                  {run.status === 'running' && (
                                    <span className="ml-2 text-violet-400">In progress…</span>
                                  )}
                                </div>
                                {run.status === 'done' && (
                                  <div className="text-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                    <span><span className="text-muted-foreground">Supplier SKUs:</span> {run.supplierSkuCount ?? '—'}</span>
                                    <span><span className="text-emerald-400">Matched:</span> {run.matchedCount ?? '—'}</span>
                                    <span><span className="text-violet-400">Updated:</span> {run.updatedCount ?? '—'}</span>
                                    {(run.unmatchedSupplierCount ?? 0) > 0 && (
                                      <span><span className="text-amber-400">Unmatched (supplier):</span> {run.unmatchedSupplierCount}</span>
                                    )}
                                    {(run.unmatchedTargetCount ?? 0) > 0 && (
                                      <span><span className="text-amber-400">Unmatched (store):</span> {run.unmatchedTargetCount}</span>
                                    )}
                                    {(run.skippedUntracked ?? 0) > 0 && (
                                      <span><span className="text-amber-400">Skipped:</span> {run.skippedUntracked}</span>
                                    )}
                                  </div>
                                )}
                                {run.status === 'error' && (
                                  <div className="text-red-400 mt-0.5 truncate">{run.errorMessage ?? 'Unknown error'}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
